import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow, parseISO } from 'date-fns'
import PropTypes from 'prop-types'
import { useMemo } from 'react'

import { Badge } from '@/components/elements/badge'
import { Skeleton } from '@/components/elements/skeleton'
import StatusIcon from '@/components/StatusIcon/StatusIcon'
import { getStudentCourseContentRecords } from '@/services/student_courses.service'

import QuizAttemptsPanel from './QuizAttemptsPanel'

const STATUS_VARIANT = {
  'Not Started': 'secondary',
  'In Progress': 'warning',
  'Quiz Pending': 'warning',
  Completed: 'success',
}

function lastActiveDate(records) {
  const dates = records
    .flatMap((r) => [r.completedAt, r.startedAt])
    .filter(Boolean)
    .map((ts) => (typeof ts === 'string' ? parseISO(ts) : ts))
    .filter((d) => !Number.isNaN(d?.getTime?.()))
  if (dates.length === 0) return null
  return dates.reduce((latest, d) => (d > latest ? d : latest))
}

function StudentCourseProgress({ courseRecord, entityId }) {
  const { data: contentRecords, isPending } = useQuery({
    // Distinct key from the student-side modal cache because this request asks
    // for expand=quiz_attempts — sharing the key would pollute student responses
    // with the (manager-only) attempt list, or vice versa.
    queryKey: ['accounts', 'instructors', 'students', 'courses', courseRecord.id, 'with-attempts'],
    queryFn: async () => {
      const response = await getStudentCourseContentRecords(entityId, courseRecord.id, {
        includeQuizAttempts: true,
      })
      return response.results
    },
  })

  const summary = useMemo(() => {
    if (!contentRecords || contentRecords.length === 0) return null
    const total = contentRecords.length
    const completed = contentRecords.filter((r) => r.status === 'Completed').length
    const inProgress = contentRecords.filter(
      (r) => r.status === 'In Progress' || r.status === 'Quiz Pending',
    ).length
    const quizCount = contentRecords.filter((r) => !!r.content?.quiz).length
    const lastActive = lastActiveDate(contentRecords)
    return { total, completed, inProgress, quizCount, lastActive }
  }, [contentRecords])

  if (isPending) {
    return (
      <div className="flex flex-col gap-4 py-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (!contentRecords || contentRecords.length === 0) {
    return <p className="py-4 text-sm text-gray-500">No course content records found.</p>
  }

  return (
    <div className="flex flex-col gap-4 py-4">
      <h3 className="text-lg font-semibold">Course Progress</h3>
      {summary && (
        <section
          aria-label="Progress summary"
          className="bg-secondary/40 grid grid-cols-2 gap-3 rounded-md border p-3 sm:grid-cols-4"
        >
          <SummaryStat
            label="Modules complete"
            value={`${summary.completed} of ${summary.total}`}
          />
          <SummaryStat label="In progress" value={summary.inProgress} />
          <SummaryStat label="Has quiz" value={summary.quizCount} />
          <SummaryStat
            label="Last active"
            value={summary.lastActive ? `${formatDistanceToNow(summary.lastActive)} ago` : 'Never'}
          />
        </section>
      )}
      {contentRecords.map((contentRecord) => {
        const hasQuiz = !!contentRecord.content?.quiz
        const name =
          contentRecord.content.type === 'Quiz Only'
            ? 'Course Quiz'
            : contentRecord.content.name || contentRecord.content.type

        return (
          <section key={contentRecord.id} className="border-b pb-4">
            <div className="flex items-center gap-3">
              <StatusIcon status={contentRecord.status || 'Not Started'} />
              <span className="font-medium">{name}</span>
              <Badge variant={STATUS_VARIANT[contentRecord.status] || 'secondary'} size="sm">
                {contentRecord.status || 'Not Started'}
              </Badge>
            </div>
            {hasQuiz && (
              <div className="mt-2 ml-8">
                <QuizAttemptsPanel
                  contentRecord={contentRecord}
                  entityId={entityId}
                  attempts={contentRecord.quizAttempts}
                />
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

function SummaryStat({ label, value }) {
  return (
    <div>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      <p className="text-muted-foreground text-xs tracking-wide uppercase">{label}</p>
    </div>
  )
}
SummaryStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default StudentCourseProgress

StudentCourseProgress.propTypes = {
  courseRecord: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
}
